package fr.frux.core.maps

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.graphics.painter.Painter
import androidx.compose.ui.unit.DpSize
import androidx.compose.ui.unit.dp
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.dsl.Feature
import org.maplibre.compose.expressions.dsl.all
import org.maplibre.compose.expressions.dsl.asNumber
import org.maplibre.compose.expressions.dsl.asString
import org.maplibre.compose.expressions.dsl.case
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.dsl.convertToString
import org.maplibre.compose.expressions.dsl.eq
import org.maplibre.compose.expressions.dsl.floor
import org.maplibre.compose.expressions.dsl.gte
import org.maplibre.compose.expressions.dsl.image
import org.maplibre.compose.expressions.dsl.offset
import org.maplibre.compose.expressions.dsl.switch
import org.maplibre.compose.expressions.dsl.times
import org.maplibre.compose.expressions.dsl.zoom
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.interaction.ClickResult
import org.maplibre.compose.layers.SymbolLayer
import org.maplibre.compose.sources.GeoJsonData
import org.maplibre.compose.sources.GeoJsonOptions
import org.maplibre.compose.sources.GeoJsonSource
import org.maplibre.compose.sources.rememberGeoJsonSource
import org.maplibre.spatialk.geojson.Point

private const val PYRAMID_TREES_LAYER_ID = "frux-pyramid-trees"
private const val PYRAMID_CLUSTER_LAYER_ID = "frux-pyramid-cluster"

private fun pyramidSatelliteLayerId(rank: Int) = "frux-pyramid-cluster-satellite-$rank"

/**
 * Points de la pyramide valables au zoom courant, de la sorte [kind]. Le filtre s'evalue au zoom
 * entier de la tuile, comme le regroupement d'une source GeoJSON qui decoupe lui aussi par tuile :
 * un niveau de la pyramide s'affiche donc exactement la ou la carte aurait montre le sien. La
 * specification de style admet `zoom` dans un filtre, evalue aux seuls zooms entiers.
 */
private fun pyramidFilter(kind: String): Expression<BooleanValue> =
    all(
        Feature[PYRAMID_KIND_PROPERTY].asString(const("")) eq const(kind),
        Feature[PYRAMID_ZOOM_PROPERTY].asNumber(const(-1f)) eq floor(zoom()),
    )

/**
 * Couches de la pyramide de regroupements (#622) : une source sans regroupement, puisque le serveur
 * a deja regroupe, et le style des regroupements du lot detaille, de sorte qu'on ne voie pas
 * passer la bascule d'un regime a l'autre. A poser juste au-dessus de la pastille du lot detaille.
 *
 * @param opacity opacite de toute la pyramide pendant le fondu avec le lot detaille. Sous
 *   [TAPPABLE_LAYER_OPACITY], ses couches laissent passer le toucher.
 * @param currentZoom zoom du SDK au moment du tap, pas le miroir applicatif de la camera, que deux
 *   taps rapproches liraient perime.
 */
@Composable
internal fun PyramidLayers(
    pyramid: MapPyramid,
    opacity: Float,
    imageCache: MarkerImageCache,
    clusterDisc: Painter,
    markerIcons: Map<String, Painter>,
    fallbackGlyph: Painter?,
    currentZoom: () -> Float,
    onClusterClick: (MapCameraPosition) -> Unit,
    onTreeClick: (MarkerId) -> Unit,
) {
    val source =
        rememberGeoJsonSource(
            data = GeoJsonData.Features(remember(pyramid) { pyramid.toFeatureCollection() }),
            options = remember(pyramid.maxZoom) { GeoJsonOptions(maxZoom = pyramid.maxZoom) },
        )
    val tappable = opacity >= TAPPABLE_LAYER_OPACITY

    PyramidTreeLayer(source, pyramid, opacity, imageCache, tappable, onTreeClick)
    PyramidSatelliteLayers(source, pyramid, opacity, markerIcons, fallbackGlyph)
    ClusterDiscLayer(
        id = PYRAMID_CLUSTER_LAYER_ID,
        source = source,
        filter = pyramidFilter(PYRAMID_KIND_CLUSTER),
        countText = Feature[PYRAMID_COUNT_PROPERTY].convertToString(),
        disc = clusterDisc,
        opacity = opacity,
        onClick = { features ->
            val cluster = features.firstOrNull()
            val position =
                (cluster?.geometry as? Point)?.coordinates?.let { coordinates ->
                    Location(latitude = coordinates.latitude, longitude = coordinates.longitude)
                }
            if (!tappable || position == null) {
                ClickResult.Pass
            } else {
                val expansionZoom =
                    cluster.properties
                        ?.get(PYRAMID_EXPANSION_PROPERTY)
                        ?.jsonPrimitive
                        ?.intOrNull
                onClusterClick(MapCameraPosition(position, pyramidTapZoom(currentZoom(), expansionZoom)))
                ClickResult.Consume
            }
        },
    )
}

/** Arbres isoles, dessines par les memes icones composites que le lot detaille. */
@Composable
private fun PyramidTreeLayer(
    source: GeoJsonSource,
    pyramid: MapPyramid,
    opacity: Float,
    imageCache: MarkerImageCache,
    tappable: Boolean,
    onTreeClick: (MarkerId) -> Unit,
) {
    val images =
        remember(pyramid, imageCache) {
            imageCache.imagesFor(pyramid.points.filterIsInstance<MapPyramidPoint.Single>().map { it.marker })
        }
    if (images.isEmpty()) return
    SymbolLayer(
        id = PYRAMID_TREES_LAYER_ID,
        source = source,
        filter = pyramidFilter(PYRAMID_KIND_SINGLE),
        iconImage = markerImageBySize(images, images.keys, COMPOSITE_MARKER_SIZE, imageCache.disc),
        iconOpacity = Feature[MARKER_ALPHA_PROPERTY].asNumber(const(1f)) * const(opacity),
        iconAllowOverlap = const(true),
        onClick = { features ->
            val id =
                features.firstNotNullOfOrNull { feature ->
                    feature.properties
                        ?.get(MARKER_ID_PROPERTY)
                        ?.jsonPrimitive
                        ?.longOrNull
                }
            if (!tappable || id == null) {
                ClickResult.Pass
            } else {
                onTreeClick(MarkerId(id))
                ClickResult.Consume
            }
        },
    )
}

/**
 * Satellites des amas : une couche par rang, comme pour le lot detaille, mais le badge de chaque
 * rang est lu tel quel dans `sat_<rang>`, classe par le serveur.
 */
@Composable
private fun PyramidSatelliteLayers(
    source: GeoJsonSource,
    pyramid: MapPyramid,
    opacity: Float,
    markerIcons: Map<String, Painter>,
    fallbackGlyph: Painter?,
) {
    val painters =
        remember(pyramid, markerIcons, fallbackGlyph) {
            pyramid.points
                .asSequence()
                .filterIsInstance<MapPyramidPoint.Cluster>()
                .flatMap { it.satellites.take(MAX_CLUSTER_SATELLITES) }
                .distinctBy(::badgeKey)
                .associate { badge -> badgeKey(badge) to satellitePainter(badge, markerIcons, fallbackGlyph) }
        }
    if (painters.isEmpty()) return
    val satelliteSize = DpSize(SATELLITE_SIZE, SATELLITE_SIZE)
    repeat(MAX_CLUSTER_SATELLITES) { rank ->
        SymbolLayer(
            id = pyramidSatelliteLayerId(rank),
            source = source,
            filter =
                all(
                    pyramidFilter(PYRAMID_KIND_CLUSTER),
                    Feature[PYRAMID_SATELLITE_COUNT_PROPERTY].asNumber(const(0f)) gte const(rank + 1f),
                ),
            iconImage =
                switch(
                    input = Feature[pyramidSatelliteProperty(rank)].asString(const("")),
                    *painters
                        .map { (key, painter) -> case(key, image(painter, size = satelliteSize)) }
                        .toTypedArray(),
                    fallback = image(painters.values.first(), size = satelliteSize),
                ),
            iconOffset =
                switch(
                    input = Feature[PYRAMID_SATELLITE_COUNT_PROPERTY].asNumber(const(0f)),
                    *(rank + 1..MAX_CLUSTER_SATELLITES)
                        .map { count ->
                            val position = satelliteOffset(rank, count)
                            case(count, offset(position.x, position.y))
                        }.toTypedArray(),
                    fallback = offset(0.dp, 0.dp),
                ),
            iconOpacity = const(opacity),
            iconAllowOverlap = const(true),
        )
    }
}
